import { Icon } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import {
	DropdownMenu,
	type DropdownDropdownItem,
	type DropdownTriggerProps,
	Button,
} from "@mdotm/shadcn/mdotm/components";

export type SortDirection = "ASC" | "DESC" | undefined;

function SortDropdownTrigger({
	direction,
	state: _state,
	...props
}: DropdownTriggerProps & { direction: SortDirection }) {
	return (
		<Button palette="tertiary" size="small" {...props}>
			<Icon
				icon={direction === "ASC" ? "sort-ascending" : direction === "DESC" ? "sort-decending" : "sorting"}
				size={18}
				color={themeCSSVars.palette_N500}
			/>
		</Button>
	);
}

/** One thing the caller can sort *by*, when the direction alone does not say it. */
export type SortByOption = { value: string; label: string };

/**
 * Sort control rendered as a dropdown (ascending / descending / clear), with a trigger icon reflecting the
 * current direction. Stateless: the caller owns the sort state and applies its own domain logic in `onChange`.
 *
 * Pass `options` when a direction is not enough to describe the sort — e.g. a stacked column, where the
 * user picks which stacked segment orders the bars. The menu then asks *what* first and *which way* second.
 */
export function SortDropdown({
	direction,
	onChange,
	disabled,
	options,
	selectedOption,
	optionsTitle = "Sort by",
}: {
	/** Direction reflected by the trigger icon. Pass `undefined` to show the neutral (unsorted) icon. */
	direction: SortDirection;
	/** `option` echoes back the entry the direction applies to — always `undefined` without `options`. */
	onChange: (direction: SortDirection, option?: string) => void;
	disabled?: boolean;
	/** What can be sorted by. The first entry stands in when a direction is picked before anything else. */
	options?: ReadonlyArray<SortByOption>;
	selectedOption?: string;
	/** @default 'Sort by' */
	optionsTitle?: string;
}): React.JSX.Element {
	const activeOption = options?.some((option) => option.value === selectedOption)
		? selectedOption
		: options?.at(0)?.value;

	const directionActions: DropdownDropdownItem[] = [
		{
			label: "Sort ascending",
			icon: "sort-ascending",
			onClick: () => onChange("ASC", activeOption),
		},
		{
			label: "Sort descending",
			icon: "sort-decending",
			onClick: () => onChange("DESC", activeOption),
		},
		{
			// With `options` there is exactly one sort to clear — this chart's own; without them the caller's
			// sort is the shared one, and clearing it clears sorting everywhere it shows.
			label: options ? "Clear sorting" : "Clear all sorting",
			// The chosen option survives a clear: it is what the next direction will apply to.
			icon: "Reload",
			onClick: () => onChange(undefined, activeOption),
		},
	];

	return (
		<DropdownMenu
			Trigger={SortDropdownTrigger}
			disabled={disabled}
			extraTriggerProps={{ direction }}
			actions={
				options && options.length > 0
					? [
							[
								optionsTitle,
								options.map(
									(option): DropdownDropdownItem => ({
										label: option.label,
										// Checkable rather than icon-marked, so the active entry reads as a selection — the same
										// tick `Select` marks its selected option with.
										checked: option.value === activeOption,
										// Picking what to sort by starts sorting: descending, i.e. heaviest segment first,
										// which is what a stacked exposure is read for.
										onClick: () => onChange(direction ?? "DESC", option.value),
									}),
								),
							],
							["", directionActions],
						]
					: directionActions
			}
		/>
	);
}
